'use client';

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Loader2, Check, Shield } from 'lucide-react';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { useAuth } from '@/lib/auth-context';
import { toast } from 'sonner';
import { Spinner } from '@/components/ui/spinner';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE;

type Membership = {
    tenantId: number;
    tenantSlug: string;
    tenantName: string;
    role: string;
};

interface TenantSwitcherProps {
    onSwitch?: () => void;
    fullWidth?: boolean;
}

const PLATFORM_SLUG = '';

export default function TenantSwitcher({ onSwitch, fullWidth }: TenantSwitcherProps) {
    const { isAuthenticated, user, login } = useAuth();
    const [memberships, setMemberships] = useState<Membership[]>([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [switchingTenantSlug, setSwitchingTenantSlug] = useState<string | null>(null);
    const [fetching, setFetching] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const isOperator = user?.role === 'OPERATOR';

    useEffect(() => {
        if (!isAuthenticated || !API_BASE) return;

        const fetchMemberships = async () => {
            setFetching(true);
            try {
                const res = await fetchWithAuth(`${API_BASE}/users/memberships`);
                if (!res.ok) {
                    throw new Error('Failed to load memberships');
                }
                const data = await res.json();
                setMemberships(data ?? []);
            } catch (err) {
                console.error('[TenantSwitcher] Failed to fetch memberships', err);
                toast.error('Unable to load tenants');
            } finally {
                setFetching(false);
            }
        };

        fetchMemberships();
    }, [isAuthenticated]);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    // Operators can always see the switcher (even without a tenant context).
    // Regular users need an active tenant slug.
    if (!isAuthenticated || (!isOperator && !user?.tenantSlug)) {
        return null;
    }

    const handleSwitch = async (tenantSlug: string) => {
        // For platform mode, compare against empty/null tenantSlug
        const currentSlug = user.tenantSlug ?? '';
        if (!API_BASE || tenantSlug === currentSlug || loading) {
            setOpen(false);
            return;
        }

        try {
            setLoading(true);
            setSwitchingTenantSlug(tenantSlug);
            const res = await fetchWithAuth(`${API_BASE}/auth/switch-tenant`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tenantSlug: tenantSlug || null }),
            });

            if (!res.ok) {
                throw new Error('Switch failed');
            }
            const data = await res.json();
            if (!data?.access_token) {
                throw new Error('No access token returned');
            }

            await login(data.access_token, { reconcileTenant: false });
            toast.success(tenantSlug ? 'Tenant switched' : 'Switched to Platform Operator');
            setOpen(false);
            onSwitch?.();
        } catch (err) {
            console.error('[TenantSwitcher] Failed to switch tenant', err);
            toast.error('Unable to switch tenant');
        } finally {
            setLoading(false);
            setSwitchingTenantSlug(null);
        }
    };

    // Build the display list: for operators, prepend the "Platform Operator" entry
    const displayEntries: Membership[] = isOperator
        ? [{ tenantId: 0, tenantSlug: PLATFORM_SLUG, tenantName: 'Platform Operator', role: 'OPERATOR' }, ...memberships]
        : memberships;

    const currentSlug = user.tenantSlug ?? '';
    const currentEntry = displayEntries.find((m) => m.tenantSlug === currentSlug);
    const buttonLabel = currentEntry?.tenantName ?? user.tenantSlug ?? 'Platform Operator';
    const disableDropdown = isOperator ? displayEntries.length < 2 : memberships.length < 2;

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={() => !disableDropdown && setOpen((prev) => !prev)}
                className={`flex items-center gap-2 rounded-[var(--radius)] border border-border bg-background/80 px-3 py-1.5 text-sm transition min-w-0 ${fullWidth ? 'w-full justify-between' : ''} ${disableDropdown ? 'cursor-default opacity-70' : 'hover:bg-muted/80'}`}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className="min-w-0 flex-1 font-semibold text-foreground truncate">{buttonLabel}</span>
                {!disableDropdown && <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />}
            </button>

            {open && !disableDropdown && (
                <div className="absolute left-0 z-50 mt-2 w-64 max-w-[calc(100vw-2rem)] rounded-[var(--radius)] border border-border bg-popover p-2 text-left shadow-[var(--shadow-brand)]">
                    {fetching ? (
                        <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading tenants...
                        </div>
                    ) : (
                        <ul className="max-h-60 overflow-y-auto" role="listbox">
                            {displayEntries.map((entry) => {
                                const isActive = entry.tenantSlug === currentSlug;
                                const isPlatform = entry.tenantSlug === PLATFORM_SLUG;
                                return (
                                    <li key={entry.tenantSlug || '__platform__'}>
                                        <button
                                            type="button"
                                            onClick={() => handleSwitch(entry.tenantSlug)}
                                            disabled={loading}
                                            className={`flex w-full items-center justify-between rounded-[var(--radius)] px-3 py-2 text-sm transition ${isActive ? 'bg-muted text-foreground' : 'hover:bg-muted/80'}`}
                                        >
                                            <div className="min-w-0 text-left flex items-center gap-2">
                                                {isPlatform && <Shield className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                                                <p className="font-medium truncate">{entry.tenantName}</p>
                                            </div>
                                            {loading && switchingTenantSlug === entry.tenantSlug ? (
                                                <Spinner className="h-4 w-4" />
                                            ) : (
                                                isActive && <Check className="h-4 w-4" />
                                            )}
                                        </button>
                                        {isPlatform && memberships.length > 0 && (
                                            <div className="my-1 border-t border-border/50" />
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
